import { useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Play, Sparkles, TrendingUp, PackageSearch } from "lucide-react";
import { scrollToSection } from "@/lib/scroll";
import { LAUNCH_APP_URL } from "@/config";
import DemoModal from "@/components/landing/DemoModal";

// Real AMRA AI Business OS Billing (Point of Sale) page screenshot.
const HERO_PREVIEW_IMG =
  "https://customer-assets-lqy194kg.emergentagent.net/job_amra-showcase/artifacts/00v53dxm_image.png";

const HEADLINE_LINES = [
  [{ t: "AMRA", accent: false }],
  [
    { t: "AI ", accent: true },
    { t: "Business OS", accent: false },
  ],
];

const lineVariants = {
  hidden: { y: "115%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.2 + i * 0.13, ease: [0.22, 1, 0.36, 1] },
  }),
};

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: 0.6 + i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

export const Hero = () => {
  const [demoOpen, setDemoOpen] = useState(false);
  const sectionRef = useRef(null);

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"],
  });
  const previewRotate = useTransform(scrollYProgress, [0, 1], [8, 0]);
  const previewY = useTransform(scrollYProgress, [0, 1], [0, -60]);
  const previewScale = useTransform(scrollYProgress, [0, 1], [1, 1.04]);

  return (
    <section
      ref={sectionRef}
      data-testid="hero-section"
      className="relative overflow-hidden px-6 pt-36 pb-20 lg:px-10 lg:pt-44"
    >
      {/* Soft premium background gradient */}
      <div
        className="pointer-events-none absolute inset-0 -z-10"
        style={{
          background:
            "radial-gradient(60% 45% at 50% 0%, rgba(13,148,136,0.10) 0%, rgba(255,255,255,0) 60%), radial-gradient(40% 30% at 85% 15%, rgba(5,150,105,0.08) 0%, rgba(255,255,255,0) 55%)",
        }}
      />

      <div className="mx-auto max-w-4xl text-center">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="inline-flex items-center gap-2 rounded-full border border-black/[0.08] bg-white/60 px-4 py-1.5 backdrop-blur-sm"
        >
          <Sparkles className="h-3.5 w-3.5 text-brand-teal" />
          <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-[#52525b]">
            AI-Native Business OS
          </span>
        </motion.div>

        <h1
          data-testid="hero-headline"
          className="mt-8 font-heading text-5xl font-bold leading-[0.95] tracking-tighter text-[#0a0a0a] sm:text-6xl lg:text-[5.5rem]"
        >
          {HEADLINE_LINES.map((line, i) => (
            <span key={i} className="reveal-mask">
              <motion.span
                custom={i}
                variants={lineVariants}
                initial="hidden"
                animate="visible"
                className="block"
              >
                {line.map((seg, j) => (
                  <span
                    key={j}
                    className={seg.accent ? "accent-gradient-text" : ""}
                  >
                    {seg.t}
                  </span>
                ))}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          custom={0}
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          className="mx-auto mt-7 max-w-2xl text-base leading-relaxed text-[#52525b] sm:text-lg"
        >
          AI-native business management &amp; billing, built from real operational
          challenges inside AMRA Decorations — not from brainstorming a startup
          idea. Software that understands your business, spots patterns, and
          recommends smarter decisions.
        </motion.p>

        <motion.div
          custom={1}
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row"
        >
          <a
            data-testid="hero-launch-app"
            href={LAUNCH_APP_URL}
            target="_blank"
            rel="noreferrer"
            className="group inline-flex w-full items-center justify-center gap-1.5 rounded-full bg-[#0a0a0a] px-6 py-3.5 text-sm font-medium text-white transition-transform duration-200 hover:scale-[1.03] sm:w-auto"
          >
            Launch App
            <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
          <button
            data-testid="hero-watch-demo"
            onClick={() => setDemoOpen(true)}
            className="group inline-flex w-full items-center justify-center gap-2 rounded-full border border-black/[0.1] bg-white px-6 py-3.5 text-sm font-medium text-[#0a0a0a] transition-colors duration-200 hover:bg-[#fafafa] sm:w-auto"
          >
            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#0a0a0a] text-white">
              <Play className="h-2.5 w-2.5 translate-x-px fill-white" />
            </span>
            Watch Demo
          </button>
        </motion.div>
      </div>

      {/* Elevated app preview with parallax + soft glow */}
      <div className="relative mx-auto mt-16 max-w-5xl [perspective:1600px]">
        <div
          className="pointer-events-none absolute -inset-x-10 -top-10 bottom-0 -z-10 blur-3xl"
          style={{
            background:
              "radial-gradient(50% 50% at 50% 40%, rgba(13,148,136,0.18) 0%, rgba(255,255,255,0) 70%)",
          }}
        />
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.8, ease: [0.22, 1, 0.36, 1] }}
          style={{
            rotateX: previewRotate,
            y: previewY,
            scale: previewScale,
            transformStyle: "preserve-3d",
          }}
          className="relative rounded-2xl border border-black/[0.08] bg-white shadow-[0_30px_80px_-20px_rgba(0,0,0,0.25)]"
        >
          {/* Browser chrome */}
          <div className="flex items-center gap-1.5 border-b border-black/[0.06] px-4 py-3">
            <span className="h-2.5 w-2.5 rounded-full bg-[#f4f4f5] ring-1 ring-black/[0.06]" />
            <span className="h-2.5 w-2.5 rounded-full bg-[#f4f4f5] ring-1 ring-black/[0.06]" />
            <span className="h-2.5 w-2.5 rounded-full bg-[#f4f4f5] ring-1 ring-black/[0.06]" />
            <div className="mx-auto flex items-center gap-2 rounded-md bg-[#fafafa] px-3 py-1 ring-1 ring-black/[0.05]">
              <span className="h-1.5 w-1.5 rounded-full bg-brand-emerald" />
              <span className="font-mono text-[10px] text-[#a1a1aa]">
                app.amra.ai
              </span>
            </div>
          </div>
          <div className="overflow-hidden rounded-b-2xl">
            <img
              data-testid="hero-app-preview"
              src={HERO_PREVIEW_IMG}
              alt="AMRA AI Business OS billing page preview"
              className="aspect-[16/9] w-full object-cover object-top"
              loading="eager"
            />
          </div>

          {/* Floating glass badge cards */}
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1.3 }}
            className="absolute -left-4 top-16 hidden items-center gap-2.5 rounded-xl border border-black/[0.06] bg-white/80 px-3.5 py-2.5 shadow-lg backdrop-blur-md sm:flex"
          >
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand-teal/10 text-brand-teal">
              <PackageSearch className="h-4 w-4" />
            </span>
            <span className="text-xs font-medium text-[#0a0a0a]">
              Inventory Insights
            </span>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1.45 }}
            className="absolute -right-4 bottom-14 hidden items-center gap-2.5 rounded-xl border border-black/[0.06] bg-white/80 px-3.5 py-2.5 shadow-lg backdrop-blur-md sm:flex"
          >
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand-emerald/10 text-brand-emerald">
              <TrendingUp className="h-4 w-4" />
            </span>
            <span className="text-xs font-medium text-[#0a0a0a]">
              Revenue Analytics
            </span>
          </motion.div>
        </motion.div>
      </div>

      <div className="mt-12 flex justify-center">
        <button
          onClick={() => scrollToSection("business-impact")}
          data-testid="hero-scroll-cue"
          className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#a1a1aa] transition-colors hover:text-[#0a0a0a]"
        >
          Scroll to explore
        </button>
      </div>

      <DemoModal open={demoOpen} onOpenChange={setDemoOpen} />
    </section>
  );
};

export default Hero;
